/*
Usage example in asset (time format YYYY-MM-DDTh:i:s):
<div
    class="js-timer timer pink"
    data-count-down-timer="2026-01-30T23:59:59"
    data-count-down-ends="The promotion is over">
    <div class="count-down">
        <span class="js-timer-days" data-counter-msg="[days] days [hours] hr [minutes] min [seconds] sec left"></span>
        <span class="js-timer-oneday" data-counter-msg="[days] day [hours] hr [minutes] min [seconds] sec left"></span>
        <span class="js-timer-nodays" data-counter-msg="[hours] hr [minutes] min [seconds] sec left"></span>
    </div>
</div>

data-count-down-ends: if present and non-empty, shown in .count-down when timer expires and the div remains visible.
If absent or empty, timer collapses to zero height and is removed from the DOM after expiry.
use modifiers to add bg/text colors: pink, cyan, aero, night
*/

'use strict';

const { queryFirst, queryAll, addClass, hasClass } = require('../domUtil');
const { HIDDEN_CLASS, ACTIVE_CLASS } = require('../constants');
const { getTimeRemaining, tokenize } = require('../util');

const TIMER_COLLAPSE_MS = 300;

function renderTick(msgDays, countDownTime, timerEl, msgNoDays, msgOneDay) {
    const t = getTimeRemaining(countDownTime);
    const template = (t.days === 0) ? msgNoDays : (t.days === 1 ? msgOneDay : msgDays);

    if (timerEl) {
        timerEl.innerHTML = tokenize(template, t, '[', ']');
    }
    return t.total <= 0;
}

/**
 * When timer expires: if data-count-down-ends has a value, show it in .count-down and keep the div.
 * If not, collapse the timer to zero height and remove from DOM.
 * @param {HTMLElement} timerEl - .js-timer element
 */
function onTimerExpired(timerEl) {
    const countDownEnds = (timerEl.dataset.countDownEnds || '').trim();
    const countDownWrap = queryFirst('.count-down', timerEl);

    if (countDownWrap && countDownEnds) {
        countDownWrap.innerHTML = countDownEnds;
        return;
    }

    if (countDownWrap) {
        countDownWrap.innerHTML = '';
    }

    const startHeight = timerEl.offsetHeight;
    timerEl.style.height = `${startHeight}px`;

    requestAnimationFrame(() => {
        timerEl.style.height = '0';
    });

    const onEnd = () => {
        timerEl.removeEventListener('transitionend', onEnd);
        timerEl.remove();
    };
    timerEl.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, TIMER_COLLAPSE_MS + 50);
}

/**
 * Initialize all timers found under the given root.
 */
function initTimers(root = document) {
    const timers = queryAll('.js-timer', root);

    if (!timers || !timers.length) return;

    for (let i = 0; i < timers.length; i++) {
        const timerEl = timers[i];
        if (hasClass(timerEl, ACTIVE_CLASS)) continue;

        const { countDownTimer: countDownTime } = timerEl.dataset || {};
        const timerDays = queryFirst('.js-timer-days', timerEl);
        const timerNoDays = queryFirst('.js-timer-nodays', timerEl);
        const timerOneDay = queryFirst('.js-timer-oneday', timerEl);

        if (!timerDays || !timerNoDays || !timerOneDay) continue;

        const { counterMsg: msgDays } = timerDays.dataset || {};
        const { counterMsg: msgNoDays } = timerNoDays.dataset || {};
        const { counterMsg: msgOneDay } = timerOneDay.dataset || {};

        if (!countDownTime) {
            addClass(timerDays, HIDDEN_CLASS);
            addClass(timerEl, ACTIVE_CLASS);
            continue;
        }

        const t = getTimeRemaining(countDownTime);

        if (t.total <= 0) {
            onTimerExpired(timerEl);
            continue;
        }

        let done = renderTick(msgDays, countDownTime, timerDays, msgNoDays, msgOneDay);

        if (!done) {
            const handle = setInterval(() => {
                done = renderTick(msgDays, countDownTime, timerDays, msgNoDays, msgOneDay);
                if (done) {
                    clearInterval(handle);
                    onTimerExpired(timerEl);
                }
            }, 1000);
        } else {
            onTimerExpired(timerEl);
        }

        addClass(timerEl, ACTIVE_CLASS);
    }
}

function init() {
    // Init on page load
    initTimers();

    document.addEventListener('slot-loaded', (e) => {
        const { target } = e;
        const hasTimers = queryAll('.js-timer', target);

        if (hasTimers?.length) {
            initTimers(target);
        }
    });
}

export default init;
